import { Controller } from "@hotwired/stimulus"
import { clamp } from "../utils/aoe2_time"
import { cropBoxStyle, pointerDeltaToSourcePx } from "../utils/aoe2_geometry"
import {
  interpolateReframe, cropHeight, clampCrop, maxWidthForPlayArea,
  upsertKeyframe, removeKeyframe,
} from "../services/aoe2_reframe"

// Crop-box (reframe) editor for the AOE2 clip review page.
//
// Drag to move / resize the 9:16 crop box, add or delete a keyframe at the
// current SOURCE frame, or type exact values into the numeric fallback inputs.
// The keyframe list is serialized into the hidden `aoe2_clip[reframe_json]`
// field as a JSON array of { frame, x, y, w } (integers, source pixels).
//
// IMPORTANT: keyframe frames are SOURCE-relative (frame 0 = start of the
// segment FILE at 30fps) and are NEVER rebased when trim changes — the
// Remotion template applies the +trimStartFrames offset at sampling time.
//
// All interpolation / clamp math lives in services/aoe2_reframe (numerically
// identical to the template); px conversions live in utils/aoe2_geometry.
//
// Events (on the enclosing `aoe2-clip-review` root):
//   - listens for `clip:timeupdate` { frame } to track the frame + re-render
//   - listens for `clip:serialize` to flush the hidden field on submit
const MIN_CROP_W = 200

export default class extends Controller {
  static targets = [
    "stage", "cropBox", "field",
    "frameLabel", "xInput", "yInput", "wInput", "keyframeCount",
  ]
  static values = {
    keyframes: { type: Array, default: [] },
    sourceWidth: { type: Number, default: 1920 },
    sourceHeight: { type: Number, default: 1080 },
    playTop: { type: Number, default: 48 },
    playBottom: { type: Number, default: 880 },
  }

  connect() {
    this.keyframes = this.keyframesValue
    this.currentFrame = 0
    this.rect = this.rectAt(0)
    this.root = this.element.closest('[data-controller~="aoe2-clip-review"]') || this.element
    this.onTimeupdate = (event) => this.timeupdate(event)
    this.onSerialize = () => this.writeField()
    this.root.addEventListener("clip:timeupdate", this.onTimeupdate)
    this.root.addEventListener("clip:serialize", this.onSerialize)
    this.render()
  }

  disconnect() {
    this.root.removeEventListener("clip:timeupdate", this.onTimeupdate)
    this.root.removeEventListener("clip:serialize", this.onSerialize)
    this.endPointer()
  }

  timeupdate(event) {
    this.currentFrame = Number(event.detail && event.detail.frame) || 0
    if (this.pointerMode) return // don't fight an in-progress drag
    this.rect = this.rectAt(this.currentFrame)
    this.render()
  }

  // --- drag / resize ----------------------------------------------------------

  startDrag(event) {
    this.beginPointer(event, "drag")
  }

  startResize(event) {
    event.stopPropagation() // the handle sits inside the crop box
    this.beginPointer(event, "resize")
  }

  beginPointer(event, mode) {
    event.preventDefault()
    this.pointerMode = mode
    this.pointerStart = { x: event.clientX, y: event.clientY, rect: { ...this.rect } }
    this.onMove = (e) => this.movePointer(e)
    this.onUp = () => { this.commit(); this.endPointer() }
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp)
  }

  movePointer(event) {
    const start = this.pointerStart
    const delta = pointerDeltaToSourcePx(
      event.clientX - start.x, event.clientY - start.y, this.displayDims()
    )
    if (this.pointerMode === "resize") {
      const w = clamp(MIN_CROP_W, this.maxCropWidth(), start.rect.w + delta.x)
      this.rect = clampCrop({ x: start.rect.x, y: start.rect.y, w }, this.constraints())
    } else {
      this.rect = clampCrop(
        { x: start.rect.x + delta.x, y: start.rect.y + delta.y, w: start.rect.w },
        this.constraints()
      )
    }
    this.render()
  }

  endPointer() {
    if (this.onMove) window.removeEventListener("pointermove", this.onMove)
    if (this.onUp) window.removeEventListener("pointerup", this.onUp)
    this.onMove = this.onUp = null
    this.pointerMode = null
  }

  // --- keyframe actions -------------------------------------------------------

  // Upsert a keyframe for the current crop rect at the current SOURCE frame.
  addKeyframe(event) {
    if (event) event.preventDefault()
    this.commit()
  }

  deleteKeyframe(event) {
    if (event) event.preventDefault()
    this.keyframes = removeKeyframe(this.keyframes, this.currentFrame)
    this.writeField()
    this.rect = this.rectAt(this.currentFrame)
    this.render()
  }

  // Numeric fallback inputs (x / y / w) — apply on change.
  applyInputs() {
    const w = clamp(MIN_CROP_W, this.maxCropWidth(), Number(this.wInputTarget.value) || this.rect.w)
    this.rect = clampCrop(
      { x: Number(this.xInputTarget.value) || 0, y: Number(this.yInputTarget.value) || 0, w },
      this.constraints()
    )
    this.commit()
  }

  commit() {
    const keyframe = {
      frame: this.currentFrame,
      x: Math.round(this.rect.x),
      y: Math.round(this.rect.y),
      w: Math.round(this.rect.w),
    }
    this.keyframes = upsertKeyframe(this.keyframes, keyframe)
    this.writeField()
    this.render()
  }

  writeField() {
    if (this.hasFieldTarget) this.fieldTarget.value = JSON.stringify(this.keyframes)
  }

  // --- rendering ----------------------------------------------------------------

  render() {
    if (this.hasCropBoxTarget) {
      const style = cropBoxStyle({ ...this.rect, h: cropHeight(this.rect.w) }, this.displayScale())
      Object.assign(this.cropBoxTarget.style, style)
    }
    this.reflectInput(this.hasXInputTarget && this.xInputTarget, this.rect.x)
    this.reflectInput(this.hasYInputTarget && this.yInputTarget, this.rect.y)
    this.reflectInput(this.hasWInputTarget && this.wInputTarget, this.rect.w)
    if (this.hasFrameLabelTarget) this.frameLabelTarget.textContent = String(this.currentFrame)
    if (this.hasKeyframeCountTarget) this.keyframeCountTarget.textContent = String(this.keyframes.length)
  }

  reflectInput(input, value) {
    if (input && document.activeElement !== input) input.value = String(Math.round(value))
  }

  // --- geometry helpers -----------------------------------------------------------

  rectAt(frame) {
    return clampCrop(interpolateReframe(frame, this.keyframes), this.constraints())
  }

  constraints() {
    return {
      sourceWidth: this.sourceWidthValue,
      playTop: this.playTopValue,
      playBottom: this.playBottomValue,
    }
  }

  maxCropWidth() {
    return Math.min(this.sourceWidthValue, maxWidthForPlayArea(this.playTopValue, this.playBottomValue))
  }

  // Uniform display scale; falls back to 1 when the stage has no layout (tests).
  displayScale() {
    const width = this.hasStageTarget ? this.stageTarget.clientWidth : 0
    return width > 0 ? width / this.sourceWidthValue : 1
  }

  displayDims() {
    const scale = this.displayScale()
    return {
      displayWidth: this.sourceWidthValue * scale,
      displayHeight: this.sourceHeightValue * scale,
      sourceWidth: this.sourceWidthValue,
      sourceHeight: this.sourceHeightValue,
    }
  }
}
